import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

const VERDICT_BADGE = {
  SUPPORTED: { className: 'bg-green-600 text-white border-transparent', label: 'SUPPORTED' },
  DIRECT: { className: 'bg-green-600 text-white border-transparent', label: 'DIRECT' },
  NEEDS_INVESTIGATION: { className: 'bg-yellow-500 text-white border-transparent', label: 'NEEDS INVESTIGATION' },
  NEEDS_PARSER_UPGRADE: { className: 'bg-orange-500 text-white border-transparent', label: 'NEEDS PARSER UPGRADE' },
  NEEDS_MAPPING_TABLE: { className: 'bg-orange-500 text-white border-transparent', label: 'NEEDS MAPPING TABLE' },
  NEEDS_SCHEMA_FIELD: { className: 'bg-orange-500 text-white border-transparent', label: 'NEEDS SCHEMA FIELD' },
  DEPENDS_ON_ABOVE: { className: 'bg-blue-500 text-white border-transparent', label: 'DEPENDS ON ABOVE' },
  BLOCKED: { className: 'bg-red-600 text-white border-transparent', label: 'BLOCKED' },
  IMPOSSIBLE: { className: 'bg-red-600 text-white border-transparent', label: 'IMPOSSIBLE' },
};

function VerdictBadge({ verdict }) {
  const b = VERDICT_BADGE[verdict] || { className: 'bg-gray-500 text-white border-transparent', label: verdict };
  return <Badge className={b.className}>{b.label}</Badge>;
}

function VerdictBlock({ verdicts }) {
  if (!verdicts) return null;
  const lines = [
    { label: 'Per-clean verification', ...verdicts.per_clean_verification },
    { label: 'Property join', ...verdicts.property_join },
    { label: 'Cost-page push', ...verdicts.cost_page_push },
    { label: 'Forecast', ...verdicts.forecast },
  ];
  return (
    <div className="border rounded p-4 bg-muted/30 space-y-2">
      <div className="text-sm font-semibold mb-2">Verdict</div>
      {lines.map((l, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <div className="w-44 shrink-0 font-medium">{l.label}</div>
          <VerdictBadge verdict={l.verdict} />
          <div className="text-muted-foreground">{l.reason}</div>
        </div>
      ))}
    </div>
  );
}

function SampleRows({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <div className="text-xs font-medium text-muted-foreground mb-1">Sample cleaning line items</div>
      <table className="w-full text-xs border">
        <thead className="bg-muted">
          <tr>
            <th className="text-left p-2 border">Property</th>
            <th className="text-left p-2 border">Date</th>
            <th className="text-left p-2 border">Supplier</th>
            <th className="text-left p-2 border">Description</th>
            <th className="text-right p-2 border">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2 border font-mono">{r.property_name_raw || '-'}</td>
              <td className="p-2 border">{r.date || '(none)'}</td>
              <td className="p-2 border">{r.supplier || '-'}</td>
              <td className="p-2 border">{r.description || '-'}</td>
              <td className="p-2 border text-right font-mono">{r.amount || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PropertyComparison({ smoobuIds, stmtIds }) {
  if (!smoobuIds?.length && !stmtIds?.length) return null;
  const maxLen = Math.max(smoobuIds?.length || 0, stmtIds?.length || 0);
  const rows = [];
  for (let i = 0; i < maxLen; i++) {
    rows.push({
      smoobu: smoobuIds?.[i] || null,
      stmt: stmtIds?.[i] || null,
    });
  }
  return (
    <div className="overflow-x-auto">
      <div className="text-xs font-medium text-muted-foreground mb-1">Property identifier comparison</div>
      <table className="w-full text-xs border">
        <thead className="bg-muted">
          <tr>
            <th className="text-left p-2 border w-1/2">Smoobu property (ID / name)</th>
            <th className="text-left p-2 border w-1/2">Statement property (ref / raw name)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="p-2 border font-mono text-xs">
                {r.smoobu ? `${r.smoobu.property_id} / ${r.smoobu.property_name}` : ''}
              </td>
              <td className="p-2 border font-mono text-xs">
                {r.stmt ? `${r.stmt.property_ref || '(none)'} / ${r.stmt.property_name_raw || '(none)'}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CleaningVerificationFeasibilityPanel() {
  const [entityId, setEntityId] = useState('eci');
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    if (!entityId || !month) {
      toast.error('Entity and month are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.cleaningVerificationFeasibility({ entity_id: entityId, month });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const q1 = result?.q1_statement_cleaning;
  const q2 = result?.q2_smoobu_checkouts;
  const q3 = result?.q3_cost_page;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-500" />
          Cleaning Verification Feasibility (UK)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Can cleaning charges on UK management statements be verified against Smoobu check-outs,
          pushed to the cost page, and forecast? Enter entity + month and press Check.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity</label>
            <input
              type="text"
              className="block mt-1 border rounded px-2 py-1 text-sm w-32"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder="e.g. eci"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Month</label>
            <input
              type="text"
              className="block mt-1 border rounded px-2 py-1 text-sm w-32"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="YYYY-MM"
            />
          </div>
          <Button onClick={runCheck} disabled={loading} size="sm">
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Check
          </Button>
        </div>

        {result && (
          <div className="space-y-6 mt-4">
            {/* Verdict block at the top */}
            <VerdictBlock verdicts={result.verdicts} />

            {/* Q1: Statement cleaning */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Q1. Statement cleaning granularity</h3>
              {!q1?.found ? (
                <p className="text-sm text-muted-foreground">{q1?.note || 'No data.'}</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Statements</div>
                      <div className="font-semibold">{q1.statement_count}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Cleaning items</div>
                      <div className="font-semibold">{q1.cleaning_item_count}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Total cleaning</div>
                      <div className="font-semibold font-mono">{q1.total_cleaning_amount ?? '-'}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Granularity</div>
                      <div className="font-semibold">{q1.granularity || '-'}</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{q1.note}</p>
                  {q1.field_path && (
                    <p className="text-xs text-muted-foreground">
                      Field path: <code className="font-mono bg-muted px-1 rounded">{q1.field_path}</code>
                      {q1.expense_fields && (
                        <span> (fields per item: {q1.expense_fields.join(', ')})</span>
                      )}
                    </p>
                  )}
                  <SampleRows rows={q1.sample_rows} />
                </div>
              )}
            </div>

            {/* Q2: Smoobu check-outs */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Q2. Smoobu check-outs and property join</h3>
              {!q2?.found ? (
                <p className="text-sm text-muted-foreground">{q2?.note || 'No data.'}</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Total check-outs</div>
                      <div className="font-semibold">{q2.reservation_count}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Properties</div>
                      <div className="font-semibold">{q2.by_property?.length || 0}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Join verdict</div>
                      <div className="font-semibold">{q2.join_assessment?.verdict || '-'}</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{q2.note}</p>
                  {q2.check_out_field && (
                    <p className="text-xs text-muted-foreground">
                      Check-out date field: <code className="font-mono bg-muted px-1 rounded">{q2.check_out_field}</code>,
                      property identifier field: <code className="font-mono bg-muted px-1 rounded">{q2.property_id_field}</code>
                    </p>
                  )}

                  {/* Per-property breakdown */}
                  {q2.by_property?.length > 0 && (
                    <div className="overflow-x-auto">
                      <div className="text-xs font-medium text-muted-foreground mb-1">Check-outs by property</div>
                      <table className="w-full text-xs border">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 border">Property ID</th>
                            <th className="text-left p-2 border">Property name</th>
                            <th className="text-right p-2 border">Check-outs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q2.by_property.map((p, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2 border font-mono">{p.property_id}</td>
                              <td className="p-2 border">{p.property_name}</td>
                              <td className="p-2 border text-right">{p.checkout_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Side-by-side property lists */}
                  <PropertyComparison
                    smoobuIds={q2.smoobu_property_ids}
                    stmtIds={q2.statement_property_ids}
                  />

                  {/* Join assessment detail */}
                  {q2.join_assessment && (
                    <div className="text-xs text-muted-foreground bg-muted/50 border rounded p-3">
                      <span className="font-medium">Join: </span>
                      {q2.join_assessment.reason}
                      {q2.join_assessment.matched?.length > 0 && (
                        <span className="block mt-1">Matched IDs: {q2.join_assessment.matched.join(', ')}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Q3: Cost page */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Q3. Cost page data model</h3>
              {!q3?.found ? (
                <p className="text-sm text-muted-foreground">{q3?.note || 'No data.'}</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Cleaning type exists</div>
                      <div className="font-semibold">{q3.has_cleaning_cost_type ? 'Yes' : 'No'}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Existing entries</div>
                      <div className="font-semibold">{q3.cleaning_costs?.length || 0}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Grain</div>
                      <div className="font-semibold text-xs">{q3.grain}</div>
                    </div>
                    <div className="bg-muted rounded p-3">
                      <div className="text-muted-foreground text-xs">Forecast field</div>
                      <div className="font-semibold">{q3.has_forecast ? 'Yes' : 'No (net-new)'}</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{q3.note}</p>

                  {q3.cleaning_costs?.length > 0 && (
                    <div className="overflow-x-auto">
                      <div className="text-xs font-medium text-muted-foreground mb-1">Existing cleaning cost entries</div>
                      <table className="w-full text-xs border">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-2 border">Property ID</th>
                            <th className="text-right p-2 border">Amount</th>
                            <th className="text-right p-2 border">Rate</th>
                            <th className="text-left p-2 border">Frequency</th>
                            <th className="text-left p-2 border">From</th>
                            <th className="text-left p-2 border">To</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q3.cleaning_costs.map((c, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-2 border font-mono">{c.property_id}</td>
                              <td className="p-2 border text-right font-mono">{c.amount ?? '-'}</td>
                              <td className="p-2 border text-right font-mono">{c.rate ?? '-'}</td>
                              <td className="p-2 border">{c.frequency}</td>
                              <td className="p-2 border">{c.effective_from || '-'}</td>
                              <td className="p-2 border">{c.effective_to || 'ongoing'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
